import React, { useState, useEffect, useRef } from 'react';
import { AppData, StoreType } from '../types';
import { StoreBadge } from '../components/StoreBadge';
import { AppIcon } from '../components/AppIcon';
import { Smartphone, Globe, Loader2, RefreshCw, Star, Layers } from 'lucide-react';
import { getGrowthClassification } from '../components/GrowthScoreGauge';
import { appCatalog, STOREFRONT_COUNTRIES } from '../services/appCatalogService';
import { CATEGORIES_LIST } from '../data/mockData';

interface AppsViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
}

export const AppsView: React.FC<AppsViewProps> = ({ onSelectApp, onCompare }) => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStore, setSelectedStore] = useState<StoreType>('all');
  const [selectedCountry, setSelectedCountry] = useState<string>(appCatalog.getSelectedCountry());
  const [sortBy, setSortBy] = useState<'growth' | 'rank' | 'rating' | 'downloads'>('growth');

  const [appsList, setAppsList] = useState<AppData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalIndexed, setTotalIndexed] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  const debounceTimer = useRef<any>(null);

  // Load category counts on mount
  useEffect(() => {
    appCatalog.getCategoryCounts().then((counts) => {
      if (counts) setCategoryCounts(counts);
    });
  }, []);

  // Fetch initial page or when filters change
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      fetchApps(1, true);
    }, 250);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search, selectedCategory, selectedStore, selectedCountry, sortBy]);

  const fetchApps = async (pageToFetch: number, reset: boolean = false) => {
    if (reset) {
      setIsLoading(true);
      setCurrentPage(1);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const sortParam =
        sortBy === 'growth'
          ? 'trending'
          : sortBy === 'rank'
          ? 'top_ranked'
          : sortBy === 'rating'
          ? 'highest_rated'
          : 'most_reviewed';

      const response = await appCatalog.searchApps({
        q: search,
        category: selectedCategory,
        store: selectedStore,
        country: selectedCountry,
        sort: sortParam as any,
        page: pageToFetch,
        limit: 30
      });

      if (reset) {
        setAppsList(response.results || []);
      } else {
        setAppsList((prev) => [...prev, ...(response.results || [])]);
      }

      setTotalCount(response.total || 0);
      setHasMore(response.hasMore);
      if (response.totalIndexed) {
        setTotalIndexed(response.totalIndexed);
      }
      setCurrentPage(pageToFetch);
    } catch (err) {
      console.warn('[AppsView] Failed to fetch apps:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchApps(currentPage + 1, false);
    }
  };

  return (
    <div id="apps_view_container" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Smartphone className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Global App Store Database</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            App Store Applications Catalog
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Search, discover, and analyze any public Apple App Store application. Real metadata, verified storefront rankings, and growth velocity signals.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right text-xs text-slate-500">
            <div>Showing <strong className="text-slate-900 font-bold">{appsList.length}</strong> of <strong className="text-slate-900 font-bold">{totalCount}</strong> results</div>
            {totalIndexed > 0 && (
              <div className="text-[11px] text-emerald-600 font-medium">{totalIndexed} apps indexed in database</div>
            )}
          </div>

          <button
            id="refresh_catalog_btn"
            onClick={() => fetchApps(1, true)}
            title="Refresh Store Catalog"
            className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-blue-600' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 bg-white p-4 rounded-3xl border border-slate-200/80 shadow-2xs">
        {/* Search */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Search Store</label>
          <input
            id="apps_search_input"
            type="text"
            placeholder="Search by name, developer, keyword..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-medium"
          />
        </div>

        {/* Storefront Country */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Storefront Country</label>
          <div className="relative">
            <select
              id="apps_storefront_country_select"
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                appCatalog.setSelectedCountry(e.target.value);
              }}
              className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none font-semibold text-slate-800 cursor-pointer"
            >
              {STOREFRONT_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Category</label>
          <select
            id="apps_category_select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
          >
            <option value="all">All Categories ({totalIndexed || appsList.length})</option>
            {CATEGORIES_LIST.filter((c) => c.slug !== 'all').map((c) => {
              const count = categoryCounts[c.name] || categoryCounts[c.slug] || 0;
              return (
                <option key={c.slug} value={c.slug}>
                  {c.name} {count > 0 ? `(${count})` : ''}
                </option>
              );
            })}
          </select>
        </div>

        {/* Store */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Store</label>
          <select
            id="apps_store_select"
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value as any)}
            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none cursor-pointer"
          >
            <option value="all">All Stores</option>
            <option value="appstore">Apple App Store (Primary)</option>
            <option value="googleplay">Google Play</option>
          </select>
        </div>

        {/* Sort */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Sort By</label>
          <select
            id="apps_sort_select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none font-semibold text-blue-600 cursor-pointer"
          >
            <option value="growth">Highest Growth Score</option>
            <option value="rank">Top Search Position</option>
            <option value="rating">Highest User Rating</option>
            <option value="downloads">Most Reviews / Downloads</option>
          </select>
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-3xl p-5 border border-slate-200/80 animate-pulse space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-200 rounded-2xl"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-slate-200 rounded-md w-3/4"></div>
                  <div className="h-3 bg-slate-100 rounded-md w-1/2"></div>
                </div>
              </div>
              <div className="h-10 bg-slate-100 rounded-xl"></div>
              <div className="h-4 bg-slate-100 rounded-md w-1/3"></div>
            </div>
          ))}
        </div>
      ) : appsList.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 space-y-3">
          <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-2xl flex items-center justify-center mx-auto">
            <Smartphone className="w-6 h-6" />
          </div>
          <h3 className="font-bold text-slate-800 text-lg">No Applications Found</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            No public apps matched &ldquo;{search}&rdquo; in the {selectedCountry} storefront. Try searching for other terms like &ldquo;Notion&rdquo;, &ldquo;Instagram&rdquo;, or change the category filter.
          </p>
          <button
            onClick={() => {
              setSearch('');
              setSelectedCategory('all');
              setSelectedStore('all');
            }}
            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        /* Apps Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {appsList.map((app) => {
            const { label, bg } = getGrowthClassification(app.growthScore);

            return (
              <div
                key={app.id}
                id={`app_card_${app.id}`}
                onClick={() => onSelectApp(app.id)}
                className="bg-white rounded-3xl p-5 border border-slate-200/80 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <AppIcon
                        app={app}
                        size="md"
                        className="w-12 h-12 rounded-2xl shadow-2xs group-hover:scale-105 transition-transform flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <h3 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors line-clamp-1">
                          {app.name}
                        </h3>
                        <p className="text-xs text-slate-400 truncate">{app.developer}</p>
                      </div>
                    </div>
                    <StoreBadge store={app.primaryStore} />
                  </div>

                  <p className="text-xs text-slate-600 line-clamp-2 mb-4 leading-relaxed">
                    {app.subtitle || app.description}
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Search Position</span>
                    <div className="flex items-center gap-1.5">
                      <strong className="text-slate-900 font-extrabold">#{app.currentRank}</strong>
                      <span className="text-slate-400 font-normal truncate max-w-[90px]">({app.category})</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-slate-700 font-bold text-xs bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span>{app.rating.toFixed(1)}</span>
                    </div>

                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${bg}`}>
                      {app.growthScore} • {label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination / Load More Button */}
      {hasMore && !isLoading && (
        <div className="flex justify-center pt-4 pb-8">
          <button
            id="load_more_apps_btn"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-900 font-bold text-xs rounded-2xl border border-slate-200 shadow-xs hover:shadow-md transition-all flex items-center gap-2"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span>Loading more apps from App Store...</span>
              </>
            ) : (
              <>
                <Layers className="w-4 h-4 text-blue-600" />
                <span>Load Next 30 Applications</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
